import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';

export type SheetRow = Record<string, string>;

export interface SheetTableData {
  columns: string[];
  rows: SheetRow[];
}

@Injectable({ providedIn: 'root' })
export class SheetTableService {
  constructor(private readonly http: HttpClient) {}

  loadTableFromCsvUrl(csvUrl: string): Observable<SheetTableData> {
    return this.http
      .get(csvUrl, { responseType: 'text' })
      .pipe(map((csvText) => this.toTableData(csvText)));
  }

  private toTableData(csvText: string): SheetTableData {
    const records = this.parseCsv(csvText).filter((record) =>
      record.some((cell) => cell.trim().length > 0)
    );

    if (!records.length) {
      return { columns: [], rows: [] };
    }

    const rawHeaders = records[0];
    const columns = this.normalizeHeaders(rawHeaders);

    const rows = records
      .slice(1)
      .map((record) => this.toRow(record, columns))
      .filter((row) => Object.values(row).some((value) => value.length > 0));

    return { columns, rows };
  }

  private toRow(record: string[], columns: string[]): SheetRow {
    const row: SheetRow = {};

    columns.forEach((column, index) => {
      row[column] = (record[index] ?? '').trim();
    });

    return row;
  }

  private normalizeHeaders(headers: string[]): string[] {
    const seen: Record<string, number> = {};

    return headers.map((header, index) => {
      const normalized = header.trim() || `Column ${index + 1}`;
      const count = (seen[normalized] ?? 0) + 1;
      seen[normalized] = count;

      return count === 1 ? normalized : `${normalized} (${count})`;
    });
  }

  private parseCsv(csvText: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentCell = '';
    let inQuotes = false;

    const pushCell = (): void => {
      currentRow.push(currentCell);
      currentCell = '';
    };

    const pushRow = (): void => {
      rows.push(currentRow);
      currentRow = [];
    };

    for (let index = 0; index < csvText.length; index += 1) {
      const character = csvText[index];
      const nextCharacter = csvText[index + 1];

      if (character === '"') {
        if (inQuotes && nextCharacter === '"') {
          currentCell += '"';
          index += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (!inQuotes && character === ',') {
        pushCell();
        continue;
      }

      if (!inQuotes && (character === '\n' || character === '\r')) {
        if (character === '\r' && nextCharacter === '\n') {
          index += 1;
        }

        pushCell();
        pushRow();
        continue;
      }

      currentCell += character;
    }

    if (currentCell.length > 0 || currentRow.length > 0) {
      pushCell();
      pushRow();
    }

    return rows;
  }
}
